import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { GetExecutiveData } from '../../../utils/company-util';
import { dateToNull } from '@src/utils/general-util';

// Helper function to calculate tenure in years (rounded down)
const calculateTenure = (startDate, endDate) => {
  if (!startDate) return 0;

  const start = new Date(startDate);
  const end = endDate || new Date();

  const diffTime = Math.abs(end - start);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  const years = diffDays / 365.25; // Account for leap years

  return Math.floor(years); // Round down to nearest whole number
};

export const getExecutiveDataReq = createAsyncThunk('getExecutiveData', async (req) => {
  const response = await GetExecutiveData(req);
  return response;
});

const ExecutiveSlice = createSlice({
  name: 'companyExecutive',
  initialState: {
    isLoadingData: true,
    executiveData: [],
    executiveAverageAge: 0,
    executiveAverageTenure: 0,
    executiveTenureLengthChart: { '0-2': 0, '3-5': 0, '6-10': 0, '11-15': 0, '> 15': 0 },
    executiveNumberCount: 0,
    executiveGenderChart: { Male: 0, Female: 0 },
    executiveIsCEO: 0,
    executiveIsChairman: 0,
    executiveIsIndependent: 0,
    executiveDataCurrentExecutiveBoard: [],
    executiveDataCurrentNonBoard: [],
    error: null,
  },
  reducers: {
    handleResetExecutiveData: {
      reducer(state) {
        state.executiveData = [];
        state.executiveDataCurrentExecutiveBoard = [];
        state.executiveDataCurrentNonBoard = [];
        state.isLoadingData = true;
        state.error = null;
        state.executiveAverageAge = 0;
        state.executiveAverageTenure = 0;
        state.executiveNumberCount = 0;
        state.executiveTenureLengthChart = { '0-2': 0, '3-5': 0, '6-10': 0, '11-15': 0, '> 15': 0 };
        state.executiveGenderChart = { Male: 0, Female: 0 };
        state.executiveIsCEO = 0;
        state.executiveIsChairman = 0;
        state.executiveIsIndependent = 0;
      },
      prepare() {
        return {
          payload: {},
        };
      },
    },
    handleSetLoading: {
      reducer(state, action) {
        state.isLoadingData = action.payload.isLoading;
      },
      prepare(isLoading) {
        return {
          payload: { isLoading },
        };
      },
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getExecutiveDataReq.pending, (state) => {
        state.isLoadingData = true;
        state.error = null;
      })
      .addCase(getExecutiveDataReq.fulfilled, (state, action) => {
        state.isLoadingData = false;

        // Check if response is an error object
        if (action.payload && action.payload.error) {
          state.error = action.payload.error?.message || 'Failed to load executive data';
          state.executiveData = [];
          state.executiveDataCurrentExecutiveBoard = [];
          state.executiveDataCurrentNonBoard = [];
          state.executiveNumberCount = 0;
          state.executiveAverageAge = 0;
          state.executiveAverageTenure = 0;
          state.executiveTenureLengthChart = { '0-2': 0, '3-5': 0, '6-10': 0, '11-15': 0, '> 15': 0 };
          state.executiveGenderChart = { Male: 0, Female: 0 };
          state.executiveIsCEO = 0;
          state.executiveIsChairman = 0;
          state.executiveIsIndependent = 0;
          return;
        }

        state.executiveData = Array.isArray(action.payload) ? action.payload : [];
        state.error = null;

        // Reset all statistics first
        state.executiveNumberCount = 0;
        state.executiveAverageAge = 0;
        state.executiveAverageTenure = 0;
        state.executiveTenureLengthChart = { '0-2': 0, '3-5': 0, '6-10': 0, '11-15': 0, '> 15': 0 };
        state.executiveGenderChart = { Male: 0, Female: 0 };
        state.executiveIsCEO = 0;
        state.executiveIsChairman = 0;
        state.executiveIsIndependent = 0;

        // Calculate executive statistics
        if (Array.isArray(action.payload) && action.payload.length > 0) {
          const today = new Date();
          today.setHours(0, 0, 0, 0);

          // Filter for current executives only (for averages) - exclude those with end dates before today
          const currentExecutives = action.payload.filter((executive) => {
            if (!executive.StartDate) return false;

            const startDate = new Date(executive.StartDate);
            const endDate = executive.EndDate ? new Date(executive.EndDate) : null;

            // Start date must be today or earlier
            if (startDate > today) return false;

            // End date must be null (ongoing) or in the future
            if (endDate && endDate < today) return false;

            return true;
          });

          // Deduplicate current executives by IndividualID to avoid double-counting
          const uniqueCurrentExecutives = currentExecutives.reduce((acc, executive) => {
            if (!acc.find((exec) => exec.IndividualID === executive.IndividualID)) {
              acc.push(executive);
            }
            return acc;
          }, []);

          // Set counters based on unique current executives
          state.executiveNumberCount = uniqueCurrentExecutives.length;

          let totalAge = 0;
          let totalTenure = 0;
          let ageCount = 0;
          let tenureCount = 0;

          uniqueCurrentExecutives.forEach((executive) => {
            // Calculate average age
            if (executive.Age && executive.Age > 0) {
              totalAge += executive.Age;
              ageCount++;
            }

            // Calculate average tenure
            if (executive.Tenure && executive.Tenure > 0) {
              totalTenure += executive.Tenure;
              tenureCount++;
            }

            // Categorize tenure length
            if (executive.Tenure !== null && executive.Tenure !== undefined) {
              if (executive.Tenure <= 2) {
                state.executiveTenureLengthChart['0-2']++;
              } else if (executive.Tenure <= 5) {
                state.executiveTenureLengthChart['3-5']++;
              } else if (executive.Tenure <= 10) {
                state.executiveTenureLengthChart['6-10']++;
              } else if (executive.Tenure <= 15) {
                state.executiveTenureLengthChart['11-15']++;
              } else {
                state.executiveTenureLengthChart['> 15']++;
              }
            }

            // Count gender distribution
            if (executive.Gender) {
              if (executive.Gender.toLowerCase() === 'male') {
                state.executiveGenderChart.Male++;
              } else if (executive.Gender.toLowerCase() === 'female') {
                state.executiveGenderChart.Female++;
              }
            }
          });

          // Calculate averages
          state.executiveAverageAge = ageCount > 0 ? Math.round(totalAge / ageCount) : 0;
          state.executiveAverageTenure = tenureCount > 0 ? Math.round((totalTenure / tenureCount) * 10) / 10 : 0;

          // Filter for current executive board appointments only (exclude those with end dates before today)
          // Include Executive Board (5) and Directors (3) who are executives
          const executiveBoardAppointments = action.payload.filter((executive) => {
            if (!executive.StartDate) return false;

            if (!dateToNull(executive.StartDate, 'dd-mmm-yy')) return false;

            if (executive.AppointmentGroupID !== 5 && executive.AppointmentGroupID !== 3) return false;

            const startDate = new Date(executive.StartDate);
            const endDate = executive.EndDate ? new Date(executive.EndDate) : null;

            if (startDate > today) return false;
            if (endDate && endDate < today) return false;

            return true;
          });

          // Process chairman appointments - merge with director appointments
          const processedBoard = [];
          const chairmanAppointments = executiveBoardAppointments.filter((exec) => exec.AppointmentType === 'Chairman');
          const nonChairmanAppointments = executiveBoardAppointments.filter(
            (exec) => exec.AppointmentType !== 'Chairman',
          );

          // Add non-chairman appointments
          nonChairmanAppointments.forEach((appointment) => {
            // Check if this person is also a chairman
            const isChairman = chairmanAppointments.some((chair) => chair.IndividualID === appointment.IndividualID);

            processedBoard.push({
              ...appointment,
              Nationality: appointment.Nationality || 'Not disclosed',
              Type: appointment.AppointmentType,
              Position: appointment.Position,
              Tenure: calculateTenure(appointment.StartDate, today),
              IsChairman: isChairman,
            });
          });

          // Add chairman appointments that don't have corresponding director appointments
          chairmanAppointments.forEach((chairmanAppointment) => {
            const hasDirectorAppointment = nonChairmanAppointments.some(
              (dir) => dir.IndividualID === chairmanAppointment.IndividualID,
            );

            if (!hasDirectorAppointment) {
              processedBoard.push({
                ...chairmanAppointment,
                Nationality: chairmanAppointment.Nationality || 'Not disclosed',
                Type: 'Director',
                Position: chairmanAppointment.Position,
                Tenure: calculateTenure(chairmanAppointment.StartDate, today),
                IsChairman: true,
              });
            }
          });

          // Sort by surname A to Z, then by first name A to Z
          processedBoard.sort((a, b) => {
            const surnameA = a.Surname || '';
            const surnameB = b.Surname || '';
            const firstNameA = a.FirstName || '';
            const firstNameB = b.FirstName || '';

            // First compare surnames
            const surnameComparison = surnameA.localeCompare(surnameB);
            if (surnameComparison !== 0) {
              return surnameComparison;
            }

            // If surnames are the same, compare first names
            return firstNameA.localeCompare(firstNameB);
          });

          state.executiveDataCurrentExecutiveBoard = processedBoard;

          // Generate Non Board data — filter by BoardType (NULL or 'Non-Board')
          const nonBoardAppointments = action.payload.filter((executive) => {
            if (!executive.StartDate) return false;

            const startDate = new Date(executive.StartDate);
            const endDate = executive.EndDate ? new Date(executive.EndDate) : null;

            // Active or ongoing
            if (startDate > today) return false;
            if (endDate && endDate < today) return false;

            // Include only if BoardType is null/undefined or 'Non-Board' (case-insensitive)
            const boardType =
              executive.BoardType && typeof executive.BoardType === 'string'
                ? executive.BoardType.trim().toLowerCase()
                : null;

            return boardType === null || boardType === 'non-board';
          });

          // Process Non Board appointments
          const processedNonBoard = nonBoardAppointments.map((appointment) => ({
            ...appointment,
            Nationality: appointment.Nationality || 'Not disclosed',
            Type: appointment.AppointmentType,
            Position: appointment.Position,
            Tenure: calculateTenure(appointment.StartDate, today),
          }));

          // Sort alphabetically
          processedNonBoard.sort((a, b) => {
            const surnameA = a.Surname || '';
            const surnameB = b.Surname || '';
            const firstNameA = a.FirstName || '';
            const firstNameB = b.FirstName || '';

            const surnameComparison = surnameA.localeCompare(surnameB);
            if (surnameComparison !== 0) return surnameComparison;
            return firstNameA.localeCompare(firstNameB);
          });

          state.executiveDataCurrentNonBoard = processedNonBoard;
        } else {
          // Reset all values if no data
          state.executiveNumberCount = 0;
          state.executiveAverageAge = 0;
          state.executiveAverageTenure = 0;
          state.executiveTenureLengthChart = { '0-2': 0, '3-5': 0, '6-10': 0, '11-15': 0, '> 15': 0 };
          state.executiveGenderChart = { Male: 0, Female: 0 };
          state.executiveDataCurrentExecutiveBoard = [];
          state.executiveDataCurrentNonBoard = [];
        }
      })
      .addCase(getExecutiveDataReq.rejected, (state, action) => {
        state.isLoadingData = false;
        state.error = action.error.message;
        state.executiveData = [];
        state.executiveDataCurrentExecutiveBoard = [];
        state.executiveDataCurrentNonBoard = [];
        state.executiveNumberCount = 0;
        state.executiveAverageAge = 0;
        state.executiveAverageTenure = 0;
        state.executiveTenureLengthChart = { '0-2': 0, '3-5': 0, '6-10': 0, '11-15': 0, '> 15': 0 };
        state.executiveGenderChart = { Male: 0, Female: 0 };
        state.executiveIsCEO = 0;
        state.executiveIsChairman = 0;
        state.executiveIsIndependent = 0;
      });
  },
});

export const { handleResetExecutiveData, handleSetLoading } = ExecutiveSlice.actions;

export default ExecutiveSlice.reducer;
